import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '@bnnvara/accounts';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer } from 'react';

import { ToggleFollowBrand } from '../../api/mutations/brandFollow.gql';
import { FetchFollowedBrands } from '../../api/queries/me/brand.gql';
import withApolloClient from '../../hocs/withApolloClient';
import { availableBrands } from './availableBrands';
import { FollowBrandContext } from './FollowBrandContext';
import { reducer } from './reducer';

/**
 * This provider needs Apollo but is placed in the `_app.js`. At that level there
 * is no Apollo provider, therefore this provider is "wrapped" by `withApolloClient`.
 *
 * The state holds two properties:
 * - `followedBrands`, the brands that are followed by the user
 * - `availableBrands`, the brands that are followable by the user
 *
 * The `availableBrands` are needed to get information about a brand, like the name and image.
 */
function FollowBrandProviderInner({ children }) {
  const initialState = {
    followedBrands: undefined,
    availableBrands,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    profile: { UID },
  } = useAccount();
  const [toggleFollowBrand] = useMutation(ToggleFollowBrand);
  const query = useQuery(FetchFollowedBrands, {
    variables: {
      userId: UID,
    },
    skip: !UID,
  });

  const value = {
    followedBrands: state.followedBrands,
    availableBrands: state.availableBrands,
    async toggleFollowBrand(brandId) {
      const oldValue = value.isBrandFollowedByUser(brandId);
      const newValue = oldValue === undefined ? true : !oldValue;

      const result = await toggleFollowBrand({
        variables: {
          brandId,
          userId: UID,
          isFollowing: newValue,
        },
      });

      if (result.data.followBrand.error !== undefined) {
        return null;
      }

      if (result.data.followBrand.isFollowing) {
        dispatch({
          type: 'ADD',
          payload: brandId,
        });
      } else {
        dispatch({ type: 'REMOVE', payload: brandId });
      }

      return result.data.followBrand.isFollowing;
    },
    setAvailableBrands(brands) {
      dispatch({ type: 'SET_AVAILABLE', payload: brands });
    },
    isBrandFollowedByUser(brandId) {
      if (Array.isArray(state.followedBrands)) {
        return (
          state.followedBrands.find((brand) => brand.id === brandId) !==
          undefined
        );
      }

      return undefined;
    },
  };

  useEffect(() => {
    if (!query.data) {
      return;
    }

    dispatch({ type: 'SET', payload: query.data?.me?.followedBrands });
  }, [query.data]);

  return (
    <FollowBrandContext.Provider value={value}>
      {children}
    </FollowBrandContext.Provider>
  );
}

FollowBrandProviderInner.propTypes = {
  children: PropTypes.node,
};

export const FollowBrandProvider = withApolloClient(FollowBrandProviderInner);
